import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostListener,
  ViewChild,  
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import Swal from 'sweetalert2';

import { ChatSessionService } from '../chat-session.service'; // Import service
import { ChatSession } from '../chat-session.service';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../environments/environment';
import { MessageService } from '../services/message.service';

@Component({
    selector: 'app-sidebar',
    imports: [CommonModule, FormsModule],
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnChanges {
  @Input() isSidebarHidden: boolean = false;
  @Input() chatSessions: ChatSession[] = [];
  isLoadingSessions: boolean = false; // True while sessions are being retrieved from the server
  @Input() currentSessionId: string | null = null;
  @Input() isMobileView: boolean = false;
  @Input() availableModels: Array<{ slug: string, name: string, isImageModel?: boolean }> = [];
  @Input() selectedModel: string = 'gpt-4.1';
  @Input() creativityValue: number = 0.5;
  @Input() selectedReasoning: string = 'medium';
  @Input() reasoningValue: number = 0.5;
  @Input() imageSize: string = '1024x1024';
  @Input() imageQuality: string = 'standard';
  @Input() imageStyle: string = 'vivid';

  @Output() newChat = new EventEmitter<void>();
  @Output() sessionLoaded = new EventEmitter<string>();
  @Output() sessionDeleted = new EventEmitter<string>();
  @Output() sessionEdited = new EventEmitter<ChatSession>();
  @Output() sessionShared = new EventEmitter<string>();
  @Output() sessionPinned = new EventEmitter<ChatSession>();
  @Output() toggleSidebarVisibility = new EventEmitter<boolean>();
  @Output() closeMobileSidebar = new EventEmitter<void>();
  @Output() modelChange = new EventEmitter<string>();
  @Output() creativityChange = new EventEmitter<number>();
  @Output() reasoningChange = new EventEmitter<string>();
  @Output() reasoningValueChange = new EventEmitter<number>();
  @Output() imageSizeChange = new EventEmitter<string>();
  @Output() imageQualityChange = new EventEmitter<string>();
  @Output() imageStyleChange = new EventEmitter<string>();

  searchTerm: string = '';
  userId = localStorage.getItem('userId');
  private isCreatingNewChat = false; // Flag to prevent multiple rapid calls
  public userEmail: string | null = null;
  public userName: string | null = null;
  editingSessionId: string | null = null;
  editedTitle: string = '';
  openMenuSessionId: string | null = null; // Track which session's menu is open
  sessionDropdownTop: number = 0;
  sessionDropdownLeft: number = 0;

  @ViewChild('modelMenuItem', { read: ElementRef }) modelMenuItem!: ElementRef;
  @ViewChild('modelSubmenu', { read: ElementRef }) modelSubmenu!: ElementRef;
  private submenuHoverTimeout: any;

  constructor(
    private http: HttpClient,
    private chatSessionService: ChatSessionService,
    private msalService: MsalService,
    private messageService: MessageService
  ) { }

  // Close dropdown menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Check if click is outside menu
    if (!target.closest('.session-menu-wrapper')) {
      this.openMenuSessionId = null;
    }
  }

  onModelChange(): void {
    this.modelChange.emit(this.selectedModel);
  }

  onCreativityChange(): void {
    this.creativityChange.emit(this.creativityValue);
  }

  onReasoningChange(): void {
    this.reasoningChange.emit(this.selectedReasoning);
  }

  onReasoningValueChange(): void {
    this.reasoningValueChange.emit(this.reasoningValue);
  }

  getCurrentModelName(): string {
    const model = this.availableModels.find(m => m.slug === this.selectedModel);
    return model ? model.name : 'GPT-4.1';
  }

  getCreativityLabel(value: number): string {
    if (value >= 0.75) {
      return 'high';
    } else if (value >= 0.25) {
      return 'medium';
    } else {
      return 'low';
    }
  }

  isGemini25Pro(): boolean {
    return this.selectedModel === 'gemini-2.5-pro' || this.selectedModel === 'gemini-3.1-pro-preview';
  }

  isImageModel(): boolean {
    const model = this.availableModels.find(m => m.slug === this.selectedModel);
    return model?.isImageModel === true;
  }

  get imageSizes(): Array<{ value: string, label: string }> {
    if (this.selectedModel === 'gpt-image-1-mini') {
      return [
        { value: '1024x1024', label: 'Square (1024×1024)' },
        { value: '1536x1024', label: 'Landscape (1536×1024)' },
        { value: '1024x1536', label: 'Portrait (1024×1536)' }
      ];
    }
    return [
      { value: '1024x1024', label: 'Square (1024×1024)' },
      { value: '1792x1024', label: 'Landscape (1792×1024)' },
      { value: '1024x1792', label: 'Portrait (1024×1792)' }
    ];
  }

  imageQualities = [
    { value: 'standard', label: 'Standard' },
    { value: 'hd', label: 'HD' }
  ];

  imageStyles = [
    { value: 'vivid', label: 'Vivid' },
    { value: 'natural', label: 'Natural' }
  ];

  onImageSizeChange(value: string): void {
    this.imageSize = value;
    this.imageSizeChange.emit(value);
  }

  onImageQualityChange(value: string): void {
    this.imageQuality = value;
    this.imageQualityChange.emit(value);
  }

  onImageStyleChange(value: string): void {
    this.imageStyle = value;
    this.imageStyleChange.emit(value);
  }

  getReasoningLabel(value: number): string {
    if (value >= 0.75) {
      return 'high';
    } else if (value >= 0.25) {
      return 'medium';
    } else {
      return 'low';
    }
  }

  reasoningValueToLabel(value: number): string {
    return this.getReasoningLabel(value);
  }

  reasoningLabelToValue(label: string): number {
    switch (label.toLowerCase()) {
      case 'high':
        return 1.0;
      case 'medium':
        return 0.5;
      case 'low':
        return 0.0;
      default:
        return 0.5;
    }
  }

  getReasoningValue(): number {
    if (this.isGemini25Pro()) {
      return this.reasoningLabelToValue(this.selectedReasoning);
    }
    return this.reasoningValue;
  }

  onReasoningSliderChange(value: number): void {
    if (this.isGemini25Pro()) {
      const label = this.getReasoningLabel(value);
      this.selectedReasoning = label;
      this.onReasoningChange();
    } else {
      this.reasoningValue = value;
      this.onReasoningValueChange();
    }
  }

  onModelMenuHover(event: MouseEvent): void {
    clearTimeout(this.submenuHoverTimeout);
    if (this.modelSubmenu && this.modelMenuItem) {
      const menuItem = this.modelMenuItem.nativeElement;
      const submenu = this.modelSubmenu.nativeElement;
      const rect = menuItem.getBoundingClientRect();

      // Show the submenu aligned with the top of the menu item
      submenu.style.top = `${rect.top}px`;
      submenu.style.left = `${rect.right + 8}px`;
      submenu.style.bottom = 'auto';
      submenu.classList.add('show');

      // Adjust if submenu goes below viewport
      setTimeout(() => {
        const submenuHeight = submenu.offsetHeight;
        const viewportHeight = window.innerHeight;
        const desiredTop = rect.top;

        if (desiredTop + submenuHeight > viewportHeight - 8) {
          // Push up so it fits, but don't go above viewport
          const adjustedTop = Math.max(8, viewportHeight - submenuHeight - 8);
          submenu.style.top = `${adjustedTop}px`;
        }
      }, 0);
    }
  }

  onSubmenuHover(): void {
    clearTimeout(this.submenuHoverTimeout);
  }

  onModelMenuLeave(): void {
    this.submenuHoverTimeout = setTimeout(() => {
      if (this.modelSubmenu) {
        this.modelSubmenu.nativeElement.classList.remove('show');
      }
    }, 100);
  }

  ngOnInit(): void {
    if (this.userId) {
      this.chatSessionService.fetchUserSessionsAndSet(this.userId);
    }
    // Subscribe to get latest sessions from the service
    this.chatSessionService.allSessions$.subscribe((sessions) => {
      this.chatSessions = sessions;
      // Don't automatically create a new chat when sessions are empty
      // Let the user start interacting first
    });

    // Reflect the service's session-loading state so the sidebar can show a
    // loading indicator instead of "No chat sessions found." during retrieval.
    this.chatSessionService.sessionsLoading$.subscribe((loading) => {
      this.isLoadingSessions = loading;
    });

    this.chatSessionService.currentSession$.subscribe((session) => {
      this.currentSessionId = session?.id || null;
    });

    // Resolve user identity for display in sidebar footer
    try {
      const account = this.msalService.instance.getActiveAccount();
      const storedEmail = localStorage.getItem('email');
      const email = account?.username || storedEmail || null;
      const nameFromAccount = account?.name && account.name.trim().length > 0 ? account.name : null;
      const nameFromEmail = email ? email.split('@')[0] : null;
      (this as any).userEmail = email;
      (this as any).userName = nameFromAccount || nameFromEmail;
    } catch (e) {
      const storedEmail = localStorage.getItem('email');
      (this as any).userEmail = storedEmail;
      (this as any).userName = storedEmail ? storedEmail.split('@')[0] : null;
    }
  }

  fetchdata() {
    if (this.userId) {
      //  this.startNewChat();
      this.fetchSessionsByUserId(this.userId);
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId'] && changes['userId'].currentValue) {
      this.fetchSessionsByUserId(changes['userId'].currentValue);
    }
  }

  get filteredChatSessions(): ChatSession[] {
    // Show all sessions from the API (they were saved because they had real content).
    // Sessions loaded with metadata_only have empty history[], so we can't filter by history content.
    // Only exclude sessions with title "New Chat" and no history (unsaved drafts).
    const validSessions = this.chatSessions.filter((session) => {
      const isUnsavedDraft = (session.name === 'New Chat' || session.title === 'New Chat')
        && (!session.history || session.history.length === 0);
      return !isUnsavedDraft;
    });

    if (!this.searchTerm) {
      return validSessions;
    }
    const term = this.searchTerm.toLowerCase();
    return validSessions.filter((session) => {
      const label = (session.name || session.title || '').toLowerCase();
      return label.includes(term);
    });
  }

  // Get pinned sessions
  get pinnedSessions(): ChatSession[] {
    return this.filteredChatSessions.filter(session => session.pinned === true);
  }

  // Get unpinned sessions
  get unpinnedSessions(): ChatSession[] {
    return this.filteredChatSessions.filter(session => !session.pinned);
  }

  // Toggle session menu open/close
  toggleSessionMenu(sessionId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.openMenuSessionId === sessionId) {
      this.openMenuSessionId = null;
    } else {
      // Calculate position based on the button's position
      const button = event.target as HTMLElement;
      const menuBtn = button.closest('.menu-btn') as HTMLElement;
      if (menuBtn) {
        const rect = menuBtn.getBoundingClientRect();
        const sidebarWrapper = menuBtn.closest('.sidebar-wrapper') as HTMLElement;
        if (sidebarWrapper) {
          const sidebarRect = sidebarWrapper.getBoundingClientRect();
          // Overlap by 20px on the sidebar for a connected feel
          this.sessionDropdownLeft = sidebarRect.right - 20;
        } else {
          this.sessionDropdownLeft = rect.right - 20;
        }
        this.sessionDropdownTop = rect.top;
      }
      this.openMenuSessionId = sessionId;
    }
  }

  // Close menu when clicking outside
  closeAllMenus(): void {
    this.openMenuSessionId = null;
  }

  // Toggle pin/unpin for a session
  togglePinSession(session: ChatSession, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const updatedSession: ChatSession = {
      ...session,
      pinned: !session.pinned,
      timestamp: new Date().toISOString()
    };

    this.chatSessionService.updateSession(updatedSession);
    this.sessionPinned.emit(updatedSession);
    this.openMenuSessionId = null;

    // Show toast notification
    this.messageService.showSessionPinned(updatedSession.pinned || false);
  }

  startNewChat(): void {
    // Prevent multiple rapid calls
    if (this.isCreatingNewChat) {
      console.log(
        'New chat creation already in progress, ignoring duplicate call'
      );
      return;
    }

    this.isCreatingNewChat = true;

    // Emit newChat event to trigger new chat preparation in reference component
    // This will not create a session immediately, just prepare the UI
    this.newChat.emit();

    // Close mobile sidebar when new chat is started on mobile
    if (this.isMobileView) {
      this.closeMobileSidebar.emit();
    }

    // Reset the flag after a short delay
    setTimeout(() => {
      this.isCreatingNewChat = false;
    }, 1000);
  }

  loadSession(sessionId: string): void {
    // Unblock session changes since the user is explicitly loading a session
    this.chatSessionService.unblockNewChatSessionChanges();

    // Just emit the event and let ReferenceComponent handle loading/fetching
    this.sessionLoaded.emit(sessionId);

    // Close mobile sidebar when session is loaded on mobile
    if (this.isMobileView) {
      this.closeMobileSidebar.emit();
    }
  }

  isSessionModelAvailable(session: ChatSession): boolean {
    if (!session.model || !this.availableModels || this.availableModels.length === 0) {
      return true; // If no model info, allow it to be loaded
    }
    return this.availableModels.some(model => model.slug === session.model);
  }

  startEditTitle(session: ChatSession, event?: Event): void {
    if (event) event.stopPropagation();
    this.openMenuSessionId = null; // Close the menu
    this.editingSessionId = session.id;
    this.editedTitle = (session.name || session.title || '').trim();
  }

  saveEditedTitle(session: ChatSession, event?: Event): void {
    if (event) event.stopPropagation();
    const newTitle = (this.editedTitle || '').trim();
    if (!newTitle) {
      // Do not allow empty titles; keep editing state
      return;
    }
    const updated: ChatSession = { ...session, name: newTitle, title: newTitle, timestamp: new Date().toISOString() };
    this.chatSessionService.updateSession(updated);
    this.sessionEdited.emit(updated);
    this.editingSessionId = null;
    this.editedTitle = '';
  }

  cancelEditTitle(event?: Event): void {
    if (event) event.stopPropagation();
    this.editingSessionId = null;
    this.editedTitle = '';
  }

  deleteSession(sessionId: string): void {
    this.openMenuSessionId = null; // Close the menu
    const session = this.chatSessions.find((s) => s.id === sessionId);
    const sessionTitle = session?.title || 'this chat';

    this.messageService.showDeleteSessionConfirmation().then((result) => {
      if (!result.isConfirmed) {
        return; // user clicked Cancel
      }

      // Perform delete
      this.chatSessionService.deleteSession(sessionId);

      // Show success toast
      this.messageService.showSessionDeleted(sessionTitle);
    });
  }


  handleSessionDeleted(sessionId: string): void {
    console.log('delete trigger ', sessionId);

    const sessionToDelete = this.chatSessions.find((s) => s.id === sessionId);
    const sessionName = sessionToDelete
      ? sessionToDelete.name
      : 'this chat session';

    this.messageService.showDeleteSessionConfirmation().then((result) => {
      if (result.isConfirmed) {
        const wasCurrentSession = this.currentSessionId === sessionId;
        this.chatSessions = this.chatSessions.filter((s) => s.id !== sessionId);

        const userId = localStorage.getItem('userId');
        if (!userId) {
          console.error('User ID not found in localStorage.');
          this.messageService.showUserIdNotFound('delete');
          return;
        }

        const nj = { id: sessionId, userId: userId, db_name: environment.dbName };

        const apiUrl = `${environment.apiBaseUrl}/sessions/delete`;

        this.http.post(apiUrl, nj).subscribe({
          next: () => {
            console.log('Session deleted successfully from API.');
            this.messageService.showSessionDeleted(sessionName);
            this.fetchSessionsByUserId(userId);
          },
          error: (err: any) => {
            console.error('Error deleting session on server:', err);
            this.messageService.showSessionDeleteError();
          },
        });
      }
    });
  }

  private fetchSessionsByUserId(userId: string): void {
    console.log("fetchSessionsByUserId:", userId);
    this.chatSessionService.fetchAllSessionsFromApi(userId).subscribe({
      next: (sessions) => {
        if (sessions.length === 0) {
          console.log('No sessions found for user, waiting for user interaction');
        }
      },
      error: (error) => {
        console.error(`Error fetching sessions for user ${userId}:`, error);
      }
    });
  }
}
